import { useEffect, useState, useRef } from "react" // 💡 useRef 추가
import AdminLayout from "@/components/admin/AdminLayout"
import { getAdminPartners, createAdminPartner, updateAdminPartner } from "@/api/partnerApi"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight} from "lucide-react";

export default function AdminPartnerPage() {
    const [partners, setPartners] = useState<any[]>([])
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)

    // 💡 인라인 주소 검색을 위한 상태와 레퍼런스 추가
    const [isPostcodeOpen, setIsPostcodeOpen] = useState(false)
    const postcodeRef = useRef<HTMLDivElement>(null)

    // 💡 페이징 상태 추가
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    // @ts-ignore
    const [totalElements, setTotalElements] = useState(0)

    // 💡 API 호출 시 currentPage를 넘겨주고 결과를 세팅합니다.
    const fetchPartners = async () => {
        try {
            const res = await getAdminPartners(currentPage, 10) // 10건씩 조회
            if (res?.data) {
                // PageResponseDto 형태에 맞게 세팅
                setPartners(res.data.content || [])
                setTotalPages(res.data.totalPages || 1)
                setTotalElements(res.data.totalElements || 0)
            }
        } catch (error) { console.error(error) }
    }

    // 💡 currentPage가 변경될 때마다 데이터를 다시 불러옵니다.
    useEffect(() => { fetchPartners() }, [currentPage])

    const [formData, setFormData] = useState({
        loginId: "", password: "", partnerName: "",
        businessNo: "", representativeName: "",
        zipCode: "", address: "", detailAddress: "",
        managerName: "", managerPhone: "", managerMobile: "", managerFax: "", managerEmail: "",
        settlementBank: "", settlementAccountHolder: "", settlementAccountNo: "",
        remarks: "", status: "ACTIVE"
    })

    // const fetchPartners = async () => {
    //     try {
    //         const res = await getAdminPartners()
    //         setPartners(res?.data || [])
    //     } catch (error) { console.error(error) }
    // }
    //
    // useEffect(() => { fetchPartners() }, [])

    const openCreateModal = () => {
        setEditingId(null)
        setFormData({
            loginId: "", password: "", partnerName: "", businessNo: "", representativeName: "",
            zipCode: "", address: "", detailAddress: "", managerName: "", managerPhone: "", managerMobile: "",
            managerFax: "", managerEmail: "", settlementBank: "", settlementAccountHolder: "",
            settlementAccountNo: "", remarks: "", status: "ACTIVE"
        })
        setIsPostcodeOpen(false) // 모달 열 때 주소창 초기화
        setIsModalOpen(true)
    }

    const openEditModal = (partner: any) => {
        setEditingId(partner.partnerId)
        setFormData({
            ...partner,
            password: "",
            zipCode: partner.zipCode || "",
            representativeName: partner.representativeName || "",
            managerPhone: partner.managerPhone || "",
            managerFax: partner.managerFax || "",
            managerEmail: partner.managerEmail || "",
            settlementBank: partner.settlementBank || "",
            settlementAccountHolder: partner.settlementAccountHolder || "",
            settlementAccountNo: partner.settlementAccountNo || "",
            remarks: partner.remarks || ""
        })
        setIsPostcodeOpen(false) // 모달 열 때 주소창 초기화
        setIsModalOpen(true)
    }

    // 💡 주소 검색창 열기/닫기 토글 함수
    const togglePostcode = () => {
        if (isPostcodeOpen) {
            setIsPostcodeOpen(false) // 이미 열려있으면 닫기
            return
        }

        const daum = (window as any).daum
        if (!daum) {
            return alert("주소 검색 스크립트가 로드되지 않았습니다. 새로고침 후 다시 시도해주세요.")
        }

        setIsPostcodeOpen(true) // 창 열기 (아래 useEffect가 감지하여 스크립트 실행)
    }

    // 💡 isPostcodeOpen이 true가 되어 div가 렌더링되면, 그 안에 다음 우편번호를 embed 합니다.
    useEffect(() => {
        if (isPostcodeOpen && postcodeRef.current) {
            const daum = (window as any).daum
            new daum.Postcode({
                oncomplete: function(data: any) {
                    setFormData(prev => ({
                        ...prev,
                        zipCode: data.zonecode,
                        address: data.address
                    }))
                    setIsPostcodeOpen(false) // 주소 선택 완료 시 인라인 창 닫기
                },
                width: '100%',
                height: '100%'
            }).embed(postcodeRef.current)
        }
    }, [isPostcodeOpen])

    const handleSave = async () => {
        if (!formData.loginId || !formData.partnerName) return alert("아이디와 화원명은 필수입니다.")

        try {
            if (editingId) {
                await updateAdminPartner(editingId, formData)
                alert("화원 정보가 수정되었습니다.")
            } else {
                if (!formData.password) return alert("신규 등록 시 초기 비밀번호를 설정해주세요.")
                await createAdminPartner(formData)
                alert("신규 파트너 화원이 등록되었습니다.")
            }
            setIsModalOpen(false)
            fetchPartners()
        } catch (error) { alert("저장에 실패했습니다.") }
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div className="flex items-end justify-between">
                    <div>
                        <h2 className="text-3xl font-bold">화원(파트너) 관리</h2>
                        <p className="mt-2 text-sm text-slate-500">주문을 중계받을 파트너 화원 계정을 발급하고 관리합니다.</p>
                    </div>
                    <Button className="rounded-xl bg-slate-900" onClick={openCreateModal}>신규 화원 등록</Button>
                </div>

                <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                    <CardContent className="p-0">
                        {/* 테이블 영역 */}
                        <table className="w-full text-sm text-left">
                            <thead className="bg-slate-50 border-b text-slate-500">
                            <tr>
                                <th className="px-6 py-4">ID</th>
                                <th className="px-6 py-4">화원명 (상호)</th>
                                <th className="px-6 py-4">로그인 아이디</th>
                                <th className="px-6 py-4">담당자</th>
                                <th className="px-6 py-4">연락처</th>
                                <th className="px-6 py-4 text-center">상태</th>
                                <th className="px-6 py-4 text-center">관리</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                            {partners.map(p => (
                                <tr key={p.partnerId} className={`hover:bg-slate-50 ${p.status !== 'ACTIVE' ? 'opacity-50' : ''}`}>
                                    <td className="px-6 py-4">{p.partnerId}</td>
                                    <td className="px-6 py-4 font-bold text-slate-900">{p.partnerName}</td>
                                    <td className="px-6 py-4 text-slate-500">{p.loginId}</td>
                                    <td className="px-6 py-4">{p.managerName}</td>
                                    <td className="px-6 py-4">{p.managerMobile}</td>
                                    <td className="px-6 py-4 text-center">
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${p.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                                                {p.status === 'ACTIVE' ? '운영중' : '정지/종료'}
                                            </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <Button variant="outline" size="sm" className="rounded-xl" onClick={() => openEditModal(p)}>수정</Button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>

                        {/* 💡 테이블 바로 밑에 페이징 UI 추가 */}
                        {totalPages > 0 && (
                            <div className="flex items-center justify-center p-6 border-t border-slate-100">
                                <div className="flex items-center gap-1">

                                    {/* 💡 1. 맨 처음 페이지로 (<<) */}
                                    <Button
                                        variant="outline"
                                        className="h-8 w-8 p-0 rounded-lg text-slate-500 hover:text-slate-900"
                                        onClick={() => setCurrentPage(1)}
                                        disabled={currentPage === 1}
                                        title="첫 페이지"
                                    >
                                        <ChevronsLeft size={16} />
                                    </Button>

                                    {/* 2. 이전 페이지로 (<) */}
                                    <Button
                                        variant="outline"
                                        className="h-8 w-8 p-0 rounded-lg text-slate-500 hover:text-slate-900"
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        title="이전 페이지"
                                    >
                                        <ChevronLeft size={16} />
                                    </Button>

                                    {/* 페이지 번호 목록 */}
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <Button
                                            key={page}
                                            variant={currentPage === page ? "default" : "ghost"}
                                            className={`h-8 w-8 p-0 rounded-lg ${currentPage === page ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                                            onClick={() => setCurrentPage(page)}
                                        >
                                            {page}
                                        </Button>
                                    ))}

                                    {/* 3. 다음 페이지로 (>) */}
                                    <Button
                                        variant="outline"
                                        className="h-8 w-8 p-0 rounded-lg text-slate-500 hover:text-slate-900"
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        title="다음 페이지"
                                    >
                                        <ChevronRight size={16} />
                                    </Button>

                                    {/* 💡 4. 맨 마지막 페이지로 (>>) */}
                                    <Button
                                        variant="outline"
                                        className="h-8 w-8 p-0 rounded-lg text-slate-500 hover:text-slate-900"
                                        onClick={() => setCurrentPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                        title="마지막 페이지"
                                    >
                                        <ChevronsRight size={16} />
                                    </Button>

                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* 등록/수정 팝업 */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setIsModalOpen(false)}>
                    <div className="w-full max-w-3xl rounded-[28px] bg-white shadow-xl flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50 rounded-t-[28px]">
                            <h2 className="text-xl font-bold">{editingId ? "화원 정보 수정" : "신규 화원 계정 발급"}</h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-2xl text-slate-400 hover:text-slate-800">&times;</button>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-8">

                            {/* 1. 기본 정보 섹션 */}
                            <section>
                                <h3 className="text-sm font-bold text-slate-800 border-b border-slate-200 pb-2 mb-4">기본 및 계정 정보</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><label className="text-sm font-semibold mb-1 block">화원명(상호) *</label><Input value={formData.partnerName} onChange={e => setFormData({...formData, partnerName: e.target.value})} className="rounded-xl" /></div>
                                    <div><label className="text-sm font-semibold mb-1 block">대표자명</label><Input value={formData.representativeName} onChange={e => setFormData({...formData, representativeName: e.target.value})} className="rounded-xl" /></div>
                                    <div><label className="text-sm font-semibold mb-1 block">사업자등록번호</label><Input value={formData.businessNo} onChange={e => setFormData({...formData, businessNo: e.target.value})} className="rounded-xl" /></div>
                                    <div></div>

                                    <div><label className="text-sm font-semibold mb-1 block text-blue-600">로그인 아이디 *</label><Input disabled={!!editingId} value={formData.loginId} onChange={e => setFormData({...formData, loginId: e.target.value})} className="rounded-xl bg-blue-50/50" /></div>
                                    <div>
                                        <label className="text-sm font-semibold mb-1 block text-blue-600">{editingId ? "비밀번호 변경 (빈칸 시 유지)" : "초기 비밀번호 *"}</label>
                                        <Input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="rounded-xl bg-blue-50/50" />
                                    </div>
                                </div>
                            </section>

                            {/* 2. 주소 및 담당자 섹션 */}
                            <section>
                                <h3 className="text-sm font-bold text-slate-800 border-b border-slate-200 pb-2 mb-4">연락처 및 주소</h3>
                                <div className="space-y-4">

                                    {/* 💡 인라인 주소 검색 영역 */}
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-sm font-semibold mb-1 block">사업장 주소</label>
                                            <div className="flex gap-2 mb-2">
                                                <Input value={formData.zipCode} readOnly placeholder="우편번호" className="w-32 rounded-xl bg-slate-50 cursor-not-allowed" />
                                                <Button
                                                    type="button"
                                                    variant={isPostcodeOpen ? "secondary" : "outline"}
                                                    className="rounded-xl"
                                                    onClick={togglePostcode}
                                                >
                                                    {isPostcodeOpen ? "검색 닫기" : "우편번호 검색"}
                                                </Button>
                                            </div>

                                            {/* 💡 주소 검색창이 들어갈 빈 div 컨테이너 */}
                                            {isPostcodeOpen && (
                                                <div
                                                    ref={postcodeRef}
                                                    className="w-full h-[400px] border border-slate-200 rounded-xl mb-3 overflow-hidden"
                                                ></div>
                                            )}

                                            <Input value={formData.address} readOnly placeholder="기본 주소" className="mb-2 rounded-xl bg-slate-50 cursor-not-allowed" />
                                            <Input value={formData.detailAddress} onChange={e => setFormData({...formData, detailAddress: e.target.value})} placeholder="상세 주소를 입력해주세요" className="rounded-xl" />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-2">
                                        <div><label className="text-sm font-semibold mb-1 block">담당자명</label><Input value={formData.managerName} onChange={e => setFormData({...formData, managerName: e.target.value})} className="rounded-xl" /></div>
                                        <div><label className="text-sm font-semibold mb-1 block">휴대폰 번호</label><Input value={formData.managerMobile} onChange={e => setFormData({...formData, managerMobile: e.target.value})} className="rounded-xl" /></div>
                                        <div><label className="text-sm font-semibold mb-1 block">일반 전화</label><Input value={formData.managerPhone} onChange={e => setFormData({...formData, managerPhone: e.target.value})} className="rounded-xl" /></div>
                                        <div><label className="text-sm font-semibold mb-1 block">팩스 번호</label><Input value={formData.managerFax} onChange={e => setFormData({...formData, managerFax: e.target.value})} className="rounded-xl" /></div>
                                        <div className="md:col-span-2"><label className="text-sm font-semibold mb-1 block">이메일</label><Input type="email" value={formData.managerEmail} onChange={e => setFormData({...formData, managerEmail: e.target.value})} className="rounded-xl" /></div>
                                    </div>
                                </div>
                            </section>

                            {/* 3. 정산 및 상태 섹션 */}
                            <section>
                                <h3 className="text-sm font-bold text-slate-800 border-b border-slate-200 pb-2 mb-4">정산 및 관리</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                                    <div><label className="text-sm font-semibold mb-1 block">은행명 (정산용)</label><Input value={formData.settlementBank} onChange={e => setFormData({...formData, settlementBank: e.target.value})} placeholder="예: 신한은행" className="rounded-xl" /></div>
                                    <div><label className="text-sm font-semibold mb-1 block">예금주 (정산용)</label><Input value={formData.settlementAccountHolder} onChange={e => setFormData({...formData, settlementAccountHolder: e.target.value})} className="rounded-xl" /></div>
                                    <div><label className="text-sm font-semibold mb-1 block">계좌번호 (정산용)</label><Input value={formData.settlementAccountNo} onChange={e => setFormData({...formData, settlementAccountNo: e.target.value})} placeholder="- 없이 입력" className="rounded-xl" /></div>
                                </div>
                                <div className="grid grid-cols-3 gap-4">
                                    <div className="col-span-2">
                                        <label className="text-sm font-semibold mb-1 block">관리자 메모 (비고)</label>
                                        <textarea
                                            value={formData.remarks}
                                            onChange={e => setFormData({...formData, remarks: e.target.value})}
                                            placeholder="본사 관리용 메모 (화원에게는 노출되지 않습니다)"
                                            className="w-full min-h-[90px] border border-slate-200 rounded-xl p-3 text-sm outline-none focus:border-slate-400 resize-y"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-semibold mb-1 block">계정 상태</label>
                                        <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} className="w-full h-11 border border-slate-200 rounded-xl px-3 outline-none focus:border-slate-400 text-sm">
                                            <option value="ACTIVE">운영중 (정상 로그인)</option>
                                            <option value="PAUSED">일시정지 (로그인 차단)</option>
                                            <option value="CLOSED">계약종료 (로그인 차단)</option>
                                        </select>
                                    </div>
                                </div>
                            </section>
                        </div>

                        <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 rounded-b-[28px]">
                            <Button variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl px-6 h-11">취소</Button>
                            <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl px-8 h-11" onClick={handleSave}>저장하기</Button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    )
}